/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { Grid, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import { ENVIRONMENT } from 'config';
import { differenceInDays, format } from 'date-fns';
import {
  selectSummaryFilterList,
  selectPaceVisualFilterList,
  selectSummaryVisualToggle,
  selectPaceVisualIsDisplay,
  selectRevenueSetIsDisplay,
  selectCxlSetIsDisplay,
  selectSummaryVisual,
  selectSummaryHotelVisual,
  // selectHotelGroupFocusOn,
  selectHotelFocusOn,
  selectEnableSecondaryHotelGroups,
  selectSummaryHotelComparison,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import { AssistantIcon, CustomToggleSwitch } from 'modules/common/components';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import summaryStyles from 'modules/dashboard/components/tab-container/summary-tab/summary.module.scss';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import SummaryFilter from 'modules/dashboard/components/tab-container/summary-tab/components/filter-panel';
import {
  selectEnableHotelGroups,
  selectHotelName,
  selectSummaryForecastFilter,
  selectTabList,
  selectTargetDate,
} from 'modules/dashboard/selectors';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import { DateTime } from 'luxon';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import useSummaryWidget from 'modules/dashboard/components/tab-container/summary-tab/hooks/use-summary-widget';
import { isEmpty } from '../../../../../../common/helpers/object';
import {
  DATE_TIME_PATTERN,
  YEAR_MONTH_DAY_PATTERN,
} from '../../../../../../common/constants/date-range';

/**
 * Summary widget report with filters
 * @param {Number} value - Summary widget tab value
 * @param {String} accessToken - Power BI access token
 * @param {Boolean} active - whether summary tab is active
 * @param {Boolean} toggleDisabled - PMS sync toggle disabled state
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {String} hotelTimezone - Timezone of selected hotel
 * @param {String} reportType - Report type of active tab
 * @returns
 */
const SummaryWidget = ({
  value,
  accessToken,
  active,
  latestDate,
  reportType,
  hotelTimezone,
  toggleDisabled,
}) => {
  useSummaryWidget();
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const summaryForecastFilter = useSelector(selectSummaryForecastFilter);
  const summaryFilterList = useSelector(selectSummaryFilterList);
  const paceVisualFilterList = useSelector(selectPaceVisualFilterList);
  const visualToggle = useSelector(selectSummaryVisualToggle);
  const targetDate = useSelector(selectTargetDate);
  const paceVisualIsDisplay = useSelector(selectPaceVisualIsDisplay);
  const revenueSetIsDisplay = useSelector(selectRevenueSetIsDisplay);
  const cxlSetIsDisplay = useSelector(selectCxlSetIsDisplay);
  const selectedHotel = useSelector(selectHotelName);
  const visuals = useSelector(selectSummaryVisual);
  const hotelVisuals = useSelector(selectSummaryHotelVisual);
  // const hotelGroupFocusOn = useSelector(selectHotelGroupFocusOn);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const isSummaryHotelComparison = useSelector(selectSummaryHotelComparison);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  const [isVisualCard1Loaded, setIsVisualCard1Loaded] = useState(false);
  const [isVisualCard2Loaded, setIsVisualCard2Loaded] = useState(false);
  const [isPaceVisualLoaded, setIsPaceVisualLoaded] = useState(false);
  const [visualToggleDisabled, setVisualToggleDisabled] = useState(false);
  const [isVisualLoading, setIsVisualLoading] = useState(false);
  //
  const [summaryV1Report, setSummaryV1Report] = useState({});
  const [summaryV2Report, setSummaryV2Report] = useState({});
  const [summaryV3Report, setSummaryV3Report] = useState({});
  //
  const [isV1Rendered, setIsV1Rendered] = useState(false);
  const [isV2Rendered, setIsV2Rendered] = useState(false);
  const [isV3Rendered, setIsV3Rendered] = useState(false);
  //
  const [isV1Processing, setIsV1Processing] = useState(false);
  const [isV2Processing, setIsV2Processing] = useState(false);
  const [isV3Processing, setIsV3Processing] = useState(false);
  //
  useEffect(() => {
    // To enable hotel comparison view based on selected hotels
    if (hotelFocusOn?.id !== selectedHotel?.id) {
      dispatch(summaryActions.setIsSummaryHotelComparison(true));
    } else if (enableSecondaryHotelGroups && !enableHotelGroups) {
      dispatch(summaryActions.setIsSummaryHotelComparison(true));
    } else if (!enableSecondaryHotelGroups && enableHotelGroups) {
      dispatch(summaryActions.setIsSummaryHotelComparison(true));
    } else {
      dispatch(summaryActions.setIsSummaryHotelComparison(false));
    }
  }, [
    enableHotelGroups,
    enableSecondaryHotelGroups,
    // hotelGroupFocusOn,
    selectedHotel,
    hotelFocusOn,
  ]);
  // To get visual details based on index
  const getVisual = (index) => {
    const found = isSummaryHotelComparison
      ? hotelVisuals?.find((visual) => visual?.order === index)
      : visuals?.find((visual) => visual?.order === index);
    return found;
  };
  // To get visual name based on index
  const getVisualName = (index) => getVisual(index)?.name;

  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    filter = summaryFilterList,
    setIsRendered = () => {},
    displayFunction = () => {}
  ) => {
    const found = getVisual(index);
    //
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(d) => setIsLoaded(d)}
          setIsRendered={(r) => setIsRendered(r)}
          tab={TAB_VALUE.SUMMARY}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // Triggered when targetDate value changes
  useEffect(() => {
    // Manage state to disable/enable visual toggle
    const lastBookingDateUtc = latestDate
      ? DateTime.fromSQL(format(new Date(latestDate), DATE_TIME_PATTERN), { zone: 'utc' })
      : '';
    const formattedLastBookingDate = latestDate
      ? lastBookingDateUtc.setZone(hotelTimezone).toFormat(YEAR_MONTH_DAY_PATTERN)
      : '';
    if (
      ENVIRONMENT.ALLOW_EXTENDED_FORECASTS?.toLowerCase() === 'true' &&
      differenceInDays(new Date(targetDate.startDate), new Date(targetDate.endDate)) === 0 &&
      differenceInDays(new Date(targetDate.endDate), new Date(formattedLastBookingDate)) >= 28
    ) {
      setVisualToggleDisabled(!summaryForecastFilter);
    } else {
      dispatch(summaryActions.setSummaryVisualToggle(false));
      setVisualToggleDisabled(true);
    }
  }, [targetDate, summaryForecastFilter, latestDate]);
  // commented out for test new implementation
  // set common visual loading status based on visual collapse
  // useEffect(() => {
  //   if (!revenueSetIsDisplay && cxlSetIsDisplay && paceVisualIsDisplay) {
  //     if (isVisualCard2Loaded && isPaceVisualLoaded) {
  //       setIsVisualLoading(true);
  //     } else {
  //       setIsVisualLoading(false);
  //     }
  //   } else if (!cxlSetIsDisplay && paceVisualIsDisplay && revenueSetIsDisplay) {
  //     if (isVisualCard1Loaded && isPaceVisualLoaded) {
  //       setIsVisualLoading(true);
  //     } else {
  //       setIsVisualLoading(false);
  //     }
  //   } else if (!paceVisualIsDisplay && revenueSetIsDisplay && cxlSetIsDisplay) {
  //     if (isVisualCard1Loaded && isVisualCard2Loaded) {
  //       setIsVisualLoading(true);
  //     } else {
  //       setIsVisualLoading(false);
  //     }
  //   } else if (!paceVisualIsDisplay && !revenueSetIsDisplay && cxlSetIsDisplay) {
  //     if (isVisualCard2Loaded) {
  //       setIsVisualLoading(true);
  //     } else {
  //       setIsVisualLoading(false);
  //     }
  //   } else if (!paceVisualIsDisplay && revenueSetIsDisplay && !cxlSetIsDisplay) {
  //     if (isVisualCard1Loaded) {
  //       setIsVisualLoading(true);
  //     } else {
  //       setIsVisualLoading(false);
  //     }
  //   } else if (paceVisualIsDisplay && !revenueSetIsDisplay && !cxlSetIsDisplay) {
  //     if (isPaceVisualLoaded) {
  //       setIsVisualLoading(true);
  //     } else {
  //       setIsVisualLoading(false);
  //     }
  //   } else if (paceVisualIsDisplay && revenueSetIsDisplay && cxlSetIsDisplay) {
  //     if (isVisualCard1Loaded && isPaceVisualLoaded && isVisualCard2Loaded) {
  //       setIsVisualLoading(true);
  //     } else {
  //       setIsVisualLoading(false);
  //     }
  //   } else if (!paceVisualIsDisplay && !revenueSetIsDisplay && !cxlSetIsDisplay) {
  //     setIsVisualLoading(true);
  //   }
  // }, [
  //   paceVisualIsDisplay,
  //   revenueSetIsDisplay,
  //   cxlSetIsDisplay,
  //   isVisualCard1Loaded,
  //   isVisualCard2Loaded,
  //   isPaceVisualLoaded,
  // ]);
  //
  useEffect(() => {
    // Calculate is Loaded  based on the given conditions
    const pair1 = revenueSetIsDisplay ? revenueSetIsDisplay && isVisualCard1Loaded : true;
    const pair2 = paceVisualIsDisplay ? paceVisualIsDisplay && isPaceVisualLoaded : true;
    const pair6 = cxlSetIsDisplay ? cxlSetIsDisplay && isVisualCard2Loaded : true;
    if (
      revenueSetIsDisplay &&
      isVisualCard1Loaded &&
      paceVisualIsDisplay &&
      isPaceVisualLoaded &&
      cxlSetIsDisplay &&
      isVisualCard2Loaded
    ) {
      setIsVisualLoading(true);
    } else {
      setIsVisualLoading(pair1 || pair2 || pair6);
    }
  }, [
    revenueSetIsDisplay,
    isVisualCard1Loaded,
    paceVisualIsDisplay,
    isPaceVisualLoaded,
    cxlSetIsDisplay,
    isVisualCard2Loaded,
  ]);
  //
  useEffect(() => {
    dispatch(summaryActions.setSummaryVisualToggle(false));
  }, [summaryForecastFilter]);
  //
  return (
    active && (
      <Grid
        container
        direction="row"
        className={styles.mainGrid}
        sx={{
          display: value === TAB_VALUE.SUMMARY ? '' : 'none',
        }}
      >
        <Grid
          item
          xs={2}
          cc={2.5}
          md={2}
          sx={{
            ml: {
              xs: 0,
              cc: 0.25,
              md: 0,
            },
          }}
        >
          <SummaryFilter
            reportType={reportType}
            visual={getWidget(
              100,
              styles.symmetricComparisonWindow,
              () => {},
              () => {},
              summaryFilterList,
              () => {},
              summaryActions.setSymmetricComparisonIsDisplay
            )}
            visualName={getVisualName(100)}
            tabValue={value}
            toggleDisabled={toggleDisabled}
            latestDate={latestDate}
            hotelTimezone={hotelTimezone}
            isLoaded={isVisualLoading}
          />
        </Grid>
        {!isEmpty(summaryFilterList) && !isEmpty(paceVisualFilterList) && (
          <Grid item xs={10} cc={9.25} md={10}>
            <Box sx={{ flexGrow: 1, position: 'relative' }}>
              <VisualGrid
                isDisplay={revenueSetIsDisplay}
                visualName={getVisualName(0)}
                visual={getWidget(
                  0,
                  summaryStyles.VisualCard,
                  setSummaryV1Report,
                  setIsVisualCard1Loaded,
                  summaryFilterList,
                  setIsV1Rendered,
                  summaryActions.setRevenueSetIsDisplay
                )}
                onClick={() => {
                  dispatch(summaryActions.setRevenueSetIsDisplay(!revenueSetIsDisplay));
                }}
                interpreterFragment={
                  <AssistantIcon
                    visualName={getVisualName(0)}
                    report={summaryV1Report}
                    isProcessing={isV1Processing}
                    setIsProcessing={setIsV1Processing}
                    disabled={!isV1Rendered || isV1Processing || !revenueSetIsDisplay}
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                }
                // isRendered={isVisualCard1Rendered}
              />
              <VisualGrid
                isDisplay={cxlSetIsDisplay}
                visualName={getVisualName(1)}
                visual={getWidget(
                  1,
                  summaryStyles.VisualCard,
                  setSummaryV3Report,
                  setIsVisualCard2Loaded,
                  summaryFilterList,
                  setIsV2Rendered,
                  summaryActions.setCxlSetIsDisplay
                )}
                onClick={() => {
                  dispatch(summaryActions.setCxlSetIsDisplay(!cxlSetIsDisplay));
                }}
                interpreterFragment={
                  <AssistantIcon
                    visualName={getVisualName(1)}
                    report={summaryV3Report}
                    isProcessing={isV3Processing}
                    setIsProcessing={setIsV3Processing}
                    disabled={!isV2Rendered || isV3Processing || !cxlSetIsDisplay || isSummaryHotelComparison}
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                }
              />
              <VisualGrid
                isDisplay={paceVisualIsDisplay}
                visualName={getVisualName(visualToggle ? 4 : 2)}
                visual={
                  <>
                    {visualToggle &&
                      getWidget(
                        4,
                        styles.paceVisual,
                        setSummaryV2Report,
                        () => {},
                        paceVisualFilterList,
                        setIsV3Rendered,
                        summaryActions.setPaceVisualIsDisplay
                      )}
                    {!visualToggle &&
                      getWidget(
                        2,
                        styles.paceVisual,
                        setSummaryV2Report,
                        setIsPaceVisualLoaded,
                        paceVisualFilterList,
                        setIsV3Rendered,
                        summaryActions.setPaceVisualIsDisplay
                      )}
                  </>
                }
                onClick={() => {
                  dispatch(summaryActions.setPaceVisualIsDisplay(!paceVisualIsDisplay));
                }}
                information="Displays a 7-day forecast for Stay Dates up to 84 days ahead. Only valid for single stay date selections."
                toggleFragment={
                  ENVIRONMENT.ALLOW_EXTENDED_FORECASTS?.toLowerCase() === 'true' && (
                    <CustomToggleSwitch
                      label="Extended Forecasts"
                      size="small"
                      labelPlacement="start"
                      onChange={(event) =>
                        dispatch(summaryActions.setSummaryVisualToggle(event.target.checked))
                      }
                      checked={visualToggle ?? false}
                      disabled={visualToggleDisabled}
                    />
                  )
                }
                interpreterFragment={
                  <AssistantIcon
                    visualName={getVisualName(visualToggle ? 4 : 2)}
                    report={summaryV2Report}
                    isProcessing={isV2Processing}
                    setIsProcessing={setIsV2Processing}
                    disabled={!isV3Rendered || isV2Processing || !paceVisualIsDisplay}
                    enableSecondaryHotelGroups={enableSecondaryHotelGroups}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                }
              />
            </Box>
          </Grid>
        )}
      </Grid>
    )
  );
};
//
export default SummaryWidget;
